import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { usePermissions } from '@/auth/usePermissions';
import type { PaymentRecord, PaymentDialogResult } from '@/types/monitoring';
import {
  useCheckTransactionCorrectionMutation,
  useCancelPaymentMutation,
  useCorrectFailedPaymentMutation,
} from './api';
import { getProviderIconSrc } from './providerIcon';

const FAILED_CODES = [3, 8, 18, 19, 44, 50];

interface Props {
  payment: PaymentRecord;
  onClose: (result: PaymentDialogResult | null) => void;
}

type View = 'details' | 'cancel' | 'correct';

export function PaymentDetailsModal({ payment, onClose }: Props) {
  const { t } = useTranslation();
  const { hasAny } = usePermissions();
  const [view, setView] = useState<View>('details');
  const [ticketId, setTicketId] = useState('');
  const [externalReference, setExternalReference] = useState('');

  const checkMutation = useCheckTransactionCorrectionMutation();
  const cancelMutation = useCancelPaymentMutation();
  const correctMutation = useCorrectFailedPaymentMutation();

  const iconSrc = getProviderIconSrc(payment.providerIdentifier);

  const handleCancelClick = async () => {
    const res = await checkMutation.mutateAsync({
      transactionId: payment.id,
      transactionType: 'PAYMENT',
    });
    if (res.code === 0) {
      setView('cancel');
    } else {
      onClose({ code: res.code, message: res.message });
    }
  };

  const handleCorrectClick = async () => {
    const res = await checkMutation.mutateAsync({
      transactionId: payment.id,
      transactionType: 'PAYMENT',
    });
    if (res.code === 0) {
      setView('correct');
    } else {
      onClose({ code: res.code, message: res.message });
    }
  };

  const handleCancelSend = async () => {
    const res = await cancelMutation.mutateAsync({
      paymentId: payment.id,
      ticketId,
    });
    onClose({ code: res.code, message: res.message, type: 'cancel' });
  };

  const handleCorrectSend = async () => {
    const res = await correctMutation.mutateAsync({
      paymentId: payment.id,
      ticketId,
      externalRef: externalReference,
    });
    onClose({ code: res.code, message: res.message, type: 'correct' });
  };

  const DetailRow = ({ label, value }: { label: string; value: string | null | undefined }) => (
    <ListItem disableGutters>
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value ?? '—'}
        </Typography>
      </Stack>
    </ListItem>
  );

  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center' }}>
        {iconSrc && (
          <Box
            component="img"
            src={iconSrc}
            alt="provider"
            sx={{ width: 32, height: 32, mr: 1, verticalAlign: 'middle' }}
          />
        )}
        {moment(payment.date).format('DD/MM/YYYY H:mm')}
      </DialogTitle>
      <DialogContent>
        {view === 'details' && (
          <>
            <List dense>
              <DetailRow
                label={t('pages.monitoring.payments.table.operation', { defaultValue: 'Operation' })}
                value={t('pages.monitoring.payments.payment', { defaultValue: 'Payment' })}
              />
              <DetailRow
                label={t('pages.monitoring.payments.table.receiver', { defaultValue: 'Receiver' })}
                value={payment.userAlias}
              />
              <DetailRow
                label={t('pages.monitoring.payments.table.sender', { defaultValue: 'Sender' })}
                value={payment.providerAccountId}
              />
              <DetailRow
                label={t('pages.monitoring.payments.table.shop', { defaultValue: 'Shop' })}
                value={payment.shop ?? 'None.'}
              />
              <DetailRow
                label={t('pages.monitoring.payments.table.operation_account_number', {
                  defaultValue: 'Account Number',
                })}
                value={payment.operatorAccountNumber}
              />
              <DetailRow
                label={t('pages.monitoring.payments.external_reference', {
                  defaultValue: 'External Reference',
                })}
                value={payment.extTransactionRef}
              />
              <DetailRow
                label={t('pages.monitoring.payments.table.amount', { defaultValue: 'Amount' })}
                value={`${payment.amount} XAF`}
              />
            </List>

            {payment.status === 0 && hasAny(['edit:cancelPayment']) && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => void handleCancelClick()}
                    disabled={checkMutation.isPending}
                  >
                    {t('pages.monitoring.payments.cancel_payment', {
                      defaultValue: 'Cancel Payment',
                    })}
                  </Button>
                </Box>
              </>
            )}

            {FAILED_CODES.includes(payment.status) && hasAny(['edit:correctFailedPayment']) && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => void handleCorrectClick()}
                    disabled={checkMutation.isPending}
                  >
                    {t('pages.monitoring.payments.correct_failed_payment', {
                      defaultValue: 'Correct Failed Payment',
                    })}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}

        {view === 'cancel' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('pages.monitoring.payments.ticket_id', { defaultValue: 'Ticket ID' })}
              value={ticketId}
              onChange={(e) => setTicketId(e.target.value)}
              fullWidth
              size="small"
            />
            <Typography variant="body2" color="warning.main">
              {t('pages.monitoring.payments.cancel_payment_confirmation', {
                defaultValue: 'Are you sure you want to cancel this payment?',
              })}
            </Typography>
            <Divider />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" color="warning" onClick={() => setView('details')}>
                {t('pages.monitoring.payments.cancel', { defaultValue: 'Back' })}
              </Button>
              <Button
                variant="contained"
                onClick={() => void handleCancelSend()}
                disabled={cancelMutation.isPending || !ticketId}
              >
                {t('pages.monitoring.payments.send', { defaultValue: 'Send' })}
              </Button>
            </Stack>
          </Stack>
        )}

        {view === 'correct' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('pages.monitoring.payments.ticket_id', { defaultValue: 'Ticket ID' })}
              value={ticketId}
              onChange={(e) => setTicketId(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={t('pages.monitoring.payments.external_reference', {
                defaultValue: 'External Reference',
              })}
              value={externalReference}
              onChange={(e) => setExternalReference(e.target.value)}
              fullWidth
              size="small"
            />
            <Typography variant="body2" color="warning.main">
              {t('pages.monitoring.payments.correct_payment_confirmation', {
                defaultValue: 'Are you sure you want to correct this payment?',
              })}
            </Typography>
            <Divider />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" color="warning" onClick={() => setView('details')}>
                {t('pages.monitoring.payments.cancel', { defaultValue: 'Back' })}
              </Button>
              <Button
                variant="contained"
                onClick={() => void handleCorrectSend()}
                disabled={correctMutation.isPending || !ticketId}
              >
                {t('pages.monitoring.payments.send', { defaultValue: 'Send' })}
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
